import { supabase } from '@/core/database/supabase';
import { flattenPermissionCatalog, permissionJsonToKeys } from '@/modules/settings/permissions/catalog/permissionCatalog';
import { enforceSettingsPermission } from '@/modules/settings/permissions/enforceSettingsPermission';
import type { Role, RoleChartItem, RoleStats } from '../types/rolesList.types';

type Row = Record<string, any>;

const highRiskKeys = new Set(
  flattenPermissionCatalog()
    .filter((item) => item.action.risk === 'Cao' || item.action.risk === 'Rất cao')
    .map((item) => item.key),
);

function countHighRiskPermissions(permissionsJson: unknown) {
  return permissionJsonToKeys(permissionsJson).filter((key) => highRiskKeys.has(key)).length;
}

export const rolesListService = {
  async getRoles(): Promise<Role[]> {
    await enforceSettingsPermission('settings.roles.view');
    const { data: roles, error: rolesError } = await supabase
      .from('roles')
      .select('*')
      .order('name');

    if (rolesError) throw rolesError;

    const { data: members, error: membersError } = await supabase
      .from('user_profiles')
      .select('role_code');

    if (membersError) throw membersError;

    const memberCounts: Record<string, number> = {};
    (members || []).forEach((member: Row) => {
      const roleCode = String(member.role_code || '');
      if (!roleCode) return;
      memberCounts[roleCode] = (memberCounts[roleCode] || 0) + 1;
    });

    return (roles || []).map((role: Row) => {
      const permissionKeys = permissionJsonToKeys(role.permissions_json);
      return {
        id: String(role.id),
        code: String(role.code || ''),
        name: String(role.name || role.code || ''),
        description: String(role.description || ''),
        memberCount: memberCounts[String(role.code || '')] || 0,
        permissionCount: permissionKeys.length,
        highRiskCount: countHighRiskPermissions(role.permissions_json),
        status: role.status === 'inactive' ? 'inactive' : 'active',
        isSystem: Boolean(role.is_system),
        createdAt: role.created_at || null,
      };
    });
  },

  async deleteRole(role: Role): Promise<void> {
    await enforceSettingsPermission('settings.roles.delete');
    if (role.isSystem) throw new Error('Không thể xóa vai trò hệ thống');
    if (role.memberCount > 0) throw new Error('Không thể xóa vai trò đang có thành viên');
    const { error } = await supabase.from('roles').delete().eq('id', role.id);
    if (error) throw error;
  },

  getStats(roles: Role[]): RoleStats {
    return {
      total: roles.length,
      custom: roles.filter((role) => !role.isSystem).length,
      system: roles.filter((role) => role.isSystem).length,
      highRisk: roles.reduce((sum, role) => sum + role.highRiskCount, 0),
    };
  },

  getChartData(roles: Role[]): RoleChartItem[] {
    const colors = ['#059669', '#0284c7', '#7c3aed', '#ea580c', '#64748b'];
    return roles
      .slice()
      .sort((a, b) => b.memberCount - a.memberCount)
      .slice(0, 5)
      .map((role, index) => ({
        name: role.name,
        value: role.memberCount,
        color: colors[index % colors.length],
      }));
  },
};
